/**
 * DailyConvergenceCard — Settings section letting users opt into a daily push
 * of one curated concept at a chosen local hour. Browser's IANA timezone is auto-detected.
 */
import React, { useEffect, useState } from "react";
import { Sunrise } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const DEFAULT_HOUR = 7;

export default function DailyConvergenceCard() {
  const [optIn, setOptIn] = useState(false);
  const [hour, setHour] = useState(DEFAULT_HOUR);
  const [tz, setTz] = useState("UTC");
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/users/me/preferences");
        if (cancelled) return;
        setOptIn(!!data.daily_convergence_opt_in);
        setHour(typeof data.daily_send_hour === "number" ? data.daily_send_hour : DEFAULT_HOUR);
        // Prefer the server value; if it's UTC, sync to the browser's detected zone so the picker is meaningful.
        const browserTz = (Intl.DateTimeFormat().resolvedOptions().timeZone) || "UTC";
        setTz(data.daily_timezone && data.daily_timezone !== "UTC" ? data.daily_timezone : browserTz);
      } catch { /* unauth — ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const save = async (patch) => {
    setBusy(true);
    try {
      await api.patch("/users/me/preferences", patch);
      toast.success("Saved.");
    } catch {
      toast.error("Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const onToggle = async (next) => {
    setOptIn(next);
    await save({
      daily_convergence_opt_in: next,
      daily_send_hour: hour,
      daily_timezone: tz,
    });
  };

  const onHour = async (h) => {
    const v = Math.max(0, Math.min(23, parseInt(h, 10) || 0));
    setHour(v);
    if (optIn) await save({ daily_send_hour: v });
  };

  const onPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/daily/preview");
      if (data?.sent > 0) toast.success(`Sent a daily push: "${data.concept.title}"`);
      else toast.error("Could not deliver — enable notifications first in the Push section above.");
    } catch {
      toast.error("Preview failed.");
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <section data-testid="daily-convergence-card" className="hairline p-6 mt-8">
      <div className="flex items-start gap-3 mb-4">
        <Sunrise size={16} className="mt-1 text-[hsl(var(--accent))]" />
        <div className="flex-1">
          <div className="serif text-xl text-foreground">Daily Convergence</div>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            Each morning at the hour you choose, the archive picks one concept you haven&apos;t seen
            and quietly pushes it to your installed app — a gentle daily practice.
          </p>
        </div>
        <label className="inline-flex items-center cursor-pointer">
          <input
            data-testid="daily-toggle"
            type="checkbox"
            className="sr-only peer"
            checked={optIn}
            disabled={busy}
            onChange={(e) => onToggle(e.target.checked)}
          />
          <span className="w-10 h-6 bg-muted peer-checked:bg-[hsl(var(--primary))] rounded-full relative transition-colors">
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-background rounded-full transition-transform ${optIn ? "translate-x-4" : ""}`} />
          </span>
        </label>
      </div>

      {optIn && (
        <div className="space-y-4 mt-4 pl-7">
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground block mb-2">
              Sent at (local time)
            </label>
            <div className="flex items-center gap-3">
              <input
                data-testid="daily-hour-input"
                type="number"
                min={0} max={23}
                value={hour}
                onChange={(e) => setHour(parseInt(e.target.value, 10) || 0)}
                onBlur={(e) => onHour(e.target.value)}
                className="input-line w-20 text-center"
              />
              <span className="serif text-muted-foreground">:00 {hour < 12 ? "am" : "pm"} ({tz})</span>
            </div>
          </div>
          <button
            data-testid="daily-preview"
            onClick={onPreview}
            disabled={previewing}
            className="reaction-pill disabled:opacity-50"
          >
            {previewing ? "Sending…" : "Preview now"}
          </button>
        </div>
      )}
    </section>
  );
}
